import React from "react";
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import type { ApiKey, ApiKeyStatus, ProductScope } from "./types";

const DAY_MS = 24 * 60 * 60 * 1000;

/** A key inside this window is worth warning about before it dies. */
export const EXPIRING_SOON_DAYS = 30;

export const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

/**
 * "3 days ago" reads faster than a date when the question is "is anyone
 * actually using this key?". The absolute date stays in the tooltip.
 */
export const formatRelative = (iso: string | null): string => {
  if (!iso) return "Never";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const days = Math.floor((Date.now() - then) / DAY_MS);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? "month" : "months"} ago`;
  const years = Math.floor(months / 12);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
};

/** Days until expiry. Every Customer API token has one — there is no never. */
export const daysUntilExpiry = (key: ApiKey): number | null => {
  const at = new Date(key.expiresAt).getTime();
  if (Number.isNaN(at)) return null;
  return Math.ceil((at - Date.now()) / DAY_MS);
};

export const isExpiringSoon = (key: ApiKey): boolean => {
  if (key.status !== "active") return false;
  const days = daysUntilExpiry(key);
  return days !== null && days <= EXPIRING_SOON_DAYS;
};

const STATUS_VARIANT: Record<
  ApiKeyStatus,
  "success" | "neutral" | "warning"
> = {
  active: "success",
  revoked: "neutral",
  expired: "warning"
};

const STATUS_LABEL: Record<ApiKeyStatus, string> = {
  active: "Active",
  revoked: "Revoked",
  expired: "Expired"
};

const SCOPE_LABEL: Record<ProductScope, string> = {
  reflector: "Reflector",
  kontroledge: "Kontrol Edge"
};

/** Human-readable product list, e.g. "Kontrol Edge, Reflector". */
export const scopesLabel = (key: ApiKey): string =>
  (key.productScopes ?? []).map((s) => SCOPE_LABEL[s] ?? s).join(", ");

const REACH_LABEL: Record<string, string> = {
  own: "This tenant",
  subtree: "Tenant + customers"
};

/** Free-text haystack for the searchbar's unkeyed terms. */
export const searchableText = (key: ApiKey): string =>
  [
    key.name,
    key.displayPrefix,
    key.tenant?.name,
    key.tenant?.publicId,
    key.level,
    scopesLabel(key),
    STATUS_LABEL[key.status]
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export const SUGGESTED_FILTER_KEYS = [
  "name",
  "tenant",
  "status",
  "product",
  "level",
  "reach"
];

/** Field lookup for keyed filters (`tenant:acme`, `status:active`, …). */
export const fieldValue = (key: ApiKey, field: string): string => {
  switch (field.toLowerCase()) {
    case "name":
      return key.name ?? "";
    case "tenant":
      return key.tenant?.name ?? "";
    case "status":
      return STATUS_LABEL[key.status];
    case "product":
      return scopesLabel(key);
    case "level":
      return key.level ?? "";
    case "reach":
      return REACH_LABEL[key.accessScope] ?? key.accessScope ?? "";
    default:
      return "";
  }
};

interface ColumnOptions {
  /** Hidden when every row belongs to the same tenant — it adds nothing. */
  showTenant: boolean;
  onRevoke: (key: ApiKey) => void;
}

export const buildApiKeyColumns = ({
  showTenant,
  onRevoke
}: ColumnOptions): GenericTableColumn<ApiKey>[] => {
  const columns: GenericTableColumn<ApiKey>[] = [
    {
      id: "name",
      header: "Name",
      accessor: "name",
      sortable: true,
      renderCell: (row) => (
        <div className="flex flex-col min-w-0">
          <span className="truncate text-semantic-text-primary">
            {row.name}
          </span>
          <code className="text-xs text-semantic-text-secondary truncate">
            {row.displayPrefix}…{row.displaySuffix}
          </code>
        </div>
      )
    },
    {
      id: "products",
      header: "Access",
      sortAccessor: (row) => scopesLabel(row),
      renderCell: (row) => (
        <div className="flex flex-col min-w-0 gap-1">
          <div className="flex flex-wrap gap-1">
            {(row.productScopes ?? []).map((scope) => (
              <GenericTag
                key={scope}
                variant="neutral"
                size="small"
                label={SCOPE_LABEL[scope] ?? scope}
              />
            ))}
          </div>
          {/* Reach is only worth a line when it is the wider one — "this
              tenant" is what everyone assumes, so saying it adds nothing. */}
          {row.accessScope === "subtree" && (
            <span className="text-xs text-semantic-text-secondary">
              {REACH_LABEL.subtree}
            </span>
          )}
        </div>
      )
    }
  ];

  if (showTenant) {
    columns.push({
      id: "tenant",
      header: "Tenant",
      sortable: true,
      sortAccessor: (row) => row.tenant?.name ?? "",
      renderCell: (row) => {
        if (!row.tenant) {
          return <span className="text-semantic-text-secondary">—</span>;
        }
        return (
          <div className="flex flex-col min-w-0">
            <span className="truncate text-semantic-text-primary">
              {row.tenant.name ?? "—"}
            </span>
            {row.tenant.level && (
              <span className="text-xs text-semantic-text-secondary">
                {row.tenant.level}
              </span>
            )}
          </div>
        );
      }
    });
  }

  columns.push(
    {
      id: "lastUsed",
      header: "Last used",
      width: "130px",
      sortAccessor: (row) => row.lastUsedAt ?? "",
      renderCell: (row) => (
        <span
          className={
            row.lastUsedAt
              ? "text-semantic-text-primary"
              : "text-semantic-text-secondary"
          }
          title={row.lastUsedAt ? formatDate(row.lastUsedAt) : "Never used"}
        >
          {formatRelative(row.lastUsedAt)}
        </span>
      )
    },
    {
      id: "expires",
      header: "Expires",
      width: "150px",
      sortAccessor: (row) => row.expiresAt ?? "",
      renderCell: (row) => {
        const days = daysUntilExpiry(row);
        const soon = isExpiringSoon(row);
        return (
          <div className="flex flex-col min-w-0">
            <span
              className={
                soon
                  ? "text-semantic-text-warning"
                  : "text-semantic-text-primary"
              }
            >
              {formatDate(row.expiresAt)}
            </span>
            {row.status === "active" && days !== null && (
              <span
                className={`text-xs ${
                  soon
                    ? "text-semantic-text-warning"
                    : "text-semantic-text-secondary"
                }`}
              >
                in {days} {days === 1 ? "day" : "days"}
              </span>
            )}
          </div>
        );
      }
    },
    {
      id: "status",
      header: "Status",
      width: "120px",
      sortAccessor: (row) => row.status,
      renderCell: (row) => (
        <GenericTag
          variant={STATUS_VARIANT[row.status]}
          size="small"
          label={STATUS_LABEL[row.status]}
        />
      )
    },
    {
      id: "actions",
      header: "",
      width: "110px",
      renderCell: (row) =>
        row.status === "revoked" ? null : (
          <button
            type="button"
            onClick={() => onRevoke(row)}
            className="text-xs text-semantic-text-error hover:underline"
            aria-label={`Revoke ${row.name}`}
          >
            Revoke
          </button>
        )
    }
  );

  return columns;
};
